"use client";
import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Thumbs, Zoom, Autoplay } from "swiper/modules";
import { ClipLoader } from "react-spinners";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css";
import "swiper/css/zoom";

interface CarouselProps {
  images: { url: string; alt: string; price?: string }[];
  detailedSwiper?: boolean;
  imageSizes?: { width: number; height: number };
  onClick?: (index: number) => void;
  initialSlideIndex?: number;
  isImgOpen?: boolean;
  showPrice?: boolean;
  showName?: boolean;
  hidePagination?: boolean;
  hideZoomIcon?: boolean;
  autoPlay?: boolean;
}

const CarouselInner = ({
  images,
  detailedSwiper,
  imageSizes,
  onClick,
  initialSlideIndex = 0,
  isImgOpen = false,
  showPrice = false,
  showName = false,
  hidePagination = false,
  autoPlay = false,
}: CarouselProps) => {
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const mainSwiperRef = useRef<SwiperType | null>(null);
  const thumbsSwiperRef = useRef<SwiperType | null>(null);

  const handleLoadingComplete = (index: number) => {
    setLoadedImages((prevLoadedImages) => [...prevLoadedImages, index]);
  };

  useEffect(() => {
    return () => {
      mainSwiperRef.current = null;
      thumbsSwiperRef.current = null;
    };
  }, []);

  return (
    <>
      <Swiper
        zoom={isImgOpen}
        initialSlide={initialSlideIndex}
        slidesPerView={1}
        loop={images && images.length > 1}
        pagination={hidePagination ? false : { clickable: true }}
        navigation={true}
        modules={[Pagination, Navigation, Thumbs, Zoom, Autoplay]}
        lazyPreloadPrevNext={1}
        className={`${images && detailedSwiper && images.length > 1 ? "swiper-detailed" : "swiper"}`}
        onSwiper={(swiper) => (mainSwiperRef.current = swiper)}
        thumbs={{ swiper: thumbsSwiperRef.current ?? null }}
        autoplay={
          autoPlay && {
            delay: 8000,
            pauseOnMouseEnter: false,
            disableOnInteraction: false,
          }
        }
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <div
              className={
                !isImgOpen ? "image-container" : "swiper-zoom-container"
              }
              onClick={() =>
                window.innerWidth > 768 && onClick && onClick(index)
              }
            >
              {!loadedImages.includes(index) && (
                <div className="spinner-container">
                  <ClipLoader className="loading-spinner" color="#04c2c9" />
                </div>
              )}
              {showName && (
                <span className="name-carousel">
                  {image.alt ? image.alt : ""}
                </span>
              )}
              <Image
                className="carousel-image"
                src={image.url}
                alt={image.alt}
                width={imageSizes?.width || 500}
                height={imageSizes?.height || 500}
                quality={100}
                onLoad={() => handleLoadingComplete(index)}
                priority={index === 0} // Priority only for first image
                loading={index === 0 ? "eager" : "lazy"} //First image prio then lazy load
              />
              {showPrice && (
                <span className="price-carousel">
                  {image.price ? image.price : "Auf Anfrage"}
                </span>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {detailedSwiper && images.length > 1 && (
        <div className="mini-carousel">
          <Swiper
            spaceBetween={10}
            slidesPerView={4}
            freeMode={true}
            watchSlidesProgress={true}
            modules={[Thumbs]}
            className="mini-swiper"
            onSwiper={(swiper) => (thumbsSwiperRef.current = swiper)}
          >
            {images.map((image, index) => (
              <SwiperSlide key={`thumb-${index}`}>
                <Image
                  className="carousel-image mini-carousel-image"
                  src={image.url}
                  alt={`Thumbnail ${image.alt}`}
                  width={300}
                  height={300}
                  quality={100}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </>
  );
};

export default CarouselInner;
